import React, { useEffect, useState } from 'react';
import axios, { ADMIN_KEY_STORAGE } from '../../helpers/axios';

export default function AdminAuthGate({ children }) {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem(ADMIN_KEY_STORAGE));
  const [keyInput, setKeyInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(authed);

  // On mount with a stored key, re-verify it once. The 401 interceptor
  // will clear the key and reload if the stored value is invalid.
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    axios
      .get('api/admin/signups/total')
      .then(() => {
        if (!cancelled) setVerifying(false);
      })
      .catch(() => {
        // Interceptor handles 401 (clears + reloads). For other errors,
        // let the user proceed; the next real call will surface the issue.
        if (!cancelled) setVerifying(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authed]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!keyInput.trim() || submitting) return;
    setSubmitting(true);
    setError('');
    // Stash temporarily so the interceptor sends it on the validation call.
    localStorage.setItem(ADMIN_KEY_STORAGE, keyInput.trim());
    try {
      await axios.get('api/admin/signups/total');
      setAuthed(true);
      setVerifying(false);
    } catch (err) {
      localStorage.removeItem(ADMIN_KEY_STORAGE);
      if (err?.response?.status === 401) {
        setError('Invalid admin key.');
      } else {
        setError(`Could not reach server (${err?.response?.status || 'network error'}).`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (authed && !verifying) return children;

  if (verifying) {
    return (
      <div style={styles.screen}>
        <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Verifying access…</div>
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <form style={styles.card} onSubmit={handleSubmit}>
        <div style={styles.brand}>
          <h1 style={styles.brandTitle}>Ace It</h1>
          <div style={styles.brandSub}>Content Manager</div>
        </div>
        <label style={styles.label}>Admin Key</label>
        <input
          type="password"
          autoFocus
          value={keyInput}
          onChange={e => setKeyInput(e.target.value)}
          placeholder="Paste admin key"
          style={styles.input}
        />
        {error && <div style={styles.error}>{error}</div>}
        <button
          type="submit"
          disabled={!keyInput.trim() || submitting}
          style={{
            ...styles.button,
            opacity: !keyInput.trim() || submitting ? 0.6 : 1,
            cursor: !keyInput.trim() || submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Verifying…' : 'Continue'}
        </button>
        <div style={styles.hint}>
          Required to create, edit, or delete content. Ask an admin if you don't have a key.
        </div>
      </form>
    </div>
  );
}

const styles = {
  screen: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8fafc',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
    padding: 32,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  brand: { textAlign: 'center', marginBottom: 8 },
  brandTitle: { margin: 0, fontSize: '1.4rem', color: '#00ADB5', fontWeight: 800 },
  brandSub: { fontSize: '0.78rem', color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase' },
  label: { fontSize: '0.78rem', fontWeight: 600, color: '#475569' },
  input: {
    padding: '10px 12px',
    border: '1.5px solid #e2e8f0',
    borderRadius: 8,
    fontSize: '0.9rem',
    outline: 'none',
  },
  button: {
    marginTop: 4,
    padding: '10px 16px',
    border: 'none',
    borderRadius: 8,
    background: '#00ADB5',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  error: {
    background: '#fef2f2',
    color: '#dc2626',
    padding: '8px 10px',
    borderRadius: 6,
    fontSize: '0.8rem',
  },
  hint: { fontSize: '0.72rem', color: '#94a3b8', textAlign: 'center', lineHeight: 1.5 },
};
